import type { Vector2Tuple, Vector3Tuple, Vector4Tuple } from 'three';

import { RGBA } from '@/package';
import type { BoxWithLabel, FrameData, Task } from '@/type';
import { toRadian } from '@/util';

import type {
  Extrinsic,
  __CES2026__CameraExtrinsic,
  __CES2026__CameraIntrinsic,
  __CES2026__LidarConfig,
} from './type';

const computeBoxCornerFactory = ({
  dimension,
  rotation,
  position,
}: Pick<BoxWithLabel, 'dimension' | 'rotation' | 'position'>) => {
  const [dx, dy, dz] = dimension;
  const [, , rz] = rotation; // Yaw (Z축 회전)만 사용
  const [cx, cy, cz] = position;

  // Half dimensions
  const l2 = dx / 2;
  const w2 = dy / 2;
  const h2 = dz / 2;

  // 8 corners relative to center (0,0,0)
  const cornersRelative = [
    [l2, w2, -h2],
    [l2, -w2, -h2],
    [-l2, -w2, -h2],
    [-l2, w2, -h2],
    [l2, w2, h2],
    [l2, -w2, h2],
    [-l2, -w2, h2],
    [-l2, w2, h2],
  ];

  // Rotation Matrix (Yaw only)
  const c = Math.cos(rz);
  const s = Math.sin(rz);

  // R_z matrix:
  // [ c -s  0 ]
  // [ s  c  0 ]
  // [ 0  0  1 ]

  return () => {
    return cornersRelative.map(p => {
      // Rotate
      const xRot = c * p[0] - s * p[1];
      const yRot = s * p[0] + c * p[1];
      const zRot = p[2]; // Z rotation doesn't change Z

      // Translate (Add global position)
      return [xRot + cx, yRot + cy, zRot + cz] as Vector3Tuple;
    });
  };
};

export const computeBoxCorner = (
  param: Pick<BoxWithLabel, 'dimension' | 'rotation' | 'position'>,
) => computeBoxCornerFactory(param)();

/**
 * LiDAR calibration을 역변환 행렬로 변환
 * 차량 좌표계 -> 라이다 좌표계 변환을 위한 역행렬
 */
export const lidarCalibrationToMatrix = (
  calibration: __CES2026__LidarConfig['calibration'],
): Extrinsic => {
  const { x, y, z, roll, pitch, yaw } = calibration;

  const rollRad = toRadian(roll);
  const pitchRad = toRadian(pitch);
  const yawRad = toRadian(yaw);

  const cr = Math.cos(rollRad);
  const sr = Math.sin(rollRad);
  const cp = Math.cos(pitchRad);
  const sp = Math.sin(pitchRad);
  const cy = Math.cos(yawRad);
  const sy = Math.sin(yawRad);

  // 역회전 행렬 계산: R^-1 = Roll^-1 * Pitch^-1 * Yaw^-1
  // 직교 행렬의 역행렬은 전치 행렬과 같음

  // Yaw 역행렬 (Z축 회전)
  // [ cos(yaw)  sin(yaw)  0 ]
  // [-sin(yaw)  cos(yaw)  0 ]
  // [    0         0      1 ]

  // Pitch 역행렬 (Y축 회전)
  // [ cos(pitch)  0  -sin(pitch) ]
  // [     0       1       0      ]
  // [ sin(pitch)  0   cos(pitch) ]

  // Roll 역행렬 (X축 회전)
  // [ 1      0           0      ]
  // [ 0  cos(roll)  sin(roll)   ]
  // [ 0 -sin(roll)  cos(roll)   ]

  // R^-1 = Rx^-1 * Ry^-1 * Rz^-1
  const r00 = cy * cp;
  const r01 = sy * cp;
  const r02 = -sp;

  const r10 = cy * sp * sr - sy * cr;
  const r11 = sy * sp * sr + cy * cr;
  const r12 = cp * sr;

  const r20 = cy * sp * cr + sy * sr;
  const r21 = sy * sp * cr - cy * sr;
  const r22 = cp * cr;

  // 위치 변환: -R^-1 * t
  const r03 = -(r00 * x + r01 * y + r02 * z);
  const r13 = -(r10 * x + r11 * y + r12 * z);
  const r23 = -(r20 * x + r21 * y + r22 * z);

  const r30 = 0;
  const r31 = 0;
  const r32 = 0;
  const r33 = 1;

  return [
    [r00, r01, r02, r03],
    [r10, r11, r12, r13],
    [r20, r21, r22, r23],
    [r30, r31, r32, r33],
  ];
};

export const transformPoint = (
  extrinsic: Extrinsic,
  point: Vector3Tuple,
  lidarCalibration?: Nullish<__CES2026__LidarConfig['calibration']>,
): Vector4Tuple => {
  let [x, y, z] = point;

  // LiDAR calibration이 있는 경우 먼저 적용
  if (lidarCalibration) {
    const lidarMatrix = lidarCalibrationToMatrix(lidarCalibration);

    // LiDAR calibration 변환 적용
    const xLidar =
      lidarMatrix[0][0] * x +
      lidarMatrix[0][1] * y +
      lidarMatrix[0][2] * z +
      lidarMatrix[0][3];

    const yLidar =
      lidarMatrix[1][0] * x +
      lidarMatrix[1][1] * y +
      lidarMatrix[1][2] * z +
      lidarMatrix[1][3];

    const zLidar =
      lidarMatrix[2][0] * x +
      lidarMatrix[2][1] * y +
      lidarMatrix[2][2] * z +
      lidarMatrix[2][3];

    x = xLidar;
    y = yLidar;
    z = zLidar;
  }

  // 카메라 extrinsic 변환 적용 (M * P)
  // extrinsic[row][col]
  const xCam =
    extrinsic[0][0] * x +
    extrinsic[0][1] * y +
    extrinsic[0][2] * z +
    extrinsic[0][3];

  const yCam =
    extrinsic[1][0] * x +
    extrinsic[1][1] * y +
    extrinsic[1][2] * z +
    extrinsic[1][3];

  const zCam =
    extrinsic[2][0] * x +
    extrinsic[2][1] * y +
    extrinsic[2][2] * z +
    extrinsic[2][3];

  const wCam =
    extrinsic[3][0] * x +
    extrinsic[3][1] * y +
    extrinsic[3][2] * z +
    extrinsic[3][3];

  return [xCam, yCam, zCam, wCam];
};

/**
 * 카메라 좌표계의 점을 2D 이미지 평면으로 투영
 */
const projectPointFromCameraSpace = (
  intrinsic: __CES2026__CameraIntrinsic,
  camPoint: Vector4Tuple,
): Nullish<Vector2Tuple> => {
  const [xCam, yCam, zCam] = camPoint;

  // 카메라 뒤쪽에 있는 점은 투영하지 않음
  if (zCam <= 0) return;

  // 정규화된 좌표계 (Normalized Image Plane) 변환
  const x = xCam / zCam;
  const y = yCam / zCam;

  const { fx, fy, cx, cy, k1, k2, k3, k4, k5, k6, p1, p2, skew } = intrinsic;

  // 렌즈 왜곡 보정 (Lens Distortion) 계산
  // r^2 계산
  const r2 = x * x + y * y;
  const r4 = r2 * r2;
  const r6 = r2 * r4;

  // 방사형 왜곡 (Radial Distortion) - Rational Model (분수 모델)
  // 분자: 1 + k1*r^2 + k2*r^4 + k3*r^6
  const radialNumerator = 1 + k1 * r2 + k2 * r4 + k3 * r6;
  // 분모: 1 + k4*r^2 + k5*r^4 + k6*r^6
  const radialDenominator = 1 + k4 * r2 + k5 * r4 + k6 * r6;

  // 분모가 0이 되는 것을 방지 (일반적인 캘리브레이션 값에서는 0이 되지 않음)
  const radialDistortion =
    radialDenominator !== 0 ? radialNumerator / radialDenominator : 1;

  // 접선형 왜곡 (Tangential Distortion)
  const xy = x * y;
  const xTangential = 2 * p1 * xy + p2 * (r2 + 2 * x * x);
  const yTangential = p1 * (r2 + 2 * y * y) + 2 * p2 * xy;

  // 왜곡이 적용된 좌표 (x'', y'')
  const xDistorted = x * radialDistortion + xTangential;
  const yDistorted = y * radialDistortion + yTangential;

  // 픽셀 좌표계로 변환 (Intrinsic Matrix 적용)
  // u = fx * x'' + skew * y'' + cx
  // v = fy * y'' + cy
  const u = fx * xDistorted + skew * yDistorted + cx;
  const v = fy * yDistorted + cy;

  return [u, v];
};

/**
 * 3D 선분을 Near Plane(z = nearZ)으로 클리핑
 * 두 점이 모두 카메라 뒤에 있으면 null 반환
 * 한 점만 뒤에 있으면 교차점을 계산하여 클리핑된 선분 반환
 */
const clipLineToNearPlane = (
  p1: Vector4Tuple,
  p2: Vector4Tuple,
  nearZ = 0.01,
): Nullish<[Vector4Tuple, Vector4Tuple]> => {
  const [x1, y1, z1, w1] = p1;
  const [x2, y2, z2, w2] = p2;

  const behind1 = z1 < nearZ;
  const behind2 = z2 < nearZ;

  // 두 점 모두 카메라 앞에 있음 - 클리핑 불필요
  if (!behind1 && !behind2) {
    return [p1, p2];
  }

  // 두 점 모두 카메라 뒤에 있음 - 선분 전체 제거
  if (behind1 && behind2) {
    return null;
  }

  // 한 점만 카메라 뒤에 있음 - 교차점 계산
  // 선분의 매개변수 방정식: P(t) = P1 + t(P2 - P1), t ∈ [0, 1]
  // z = z1 + t(z2 - z1) = nearZ를 만족하는 t 찾기
  const t = (nearZ - z1) / (z2 - z1);

  // 교차점 계산
  const xIntersect = x1 + t * (x2 - x1);
  const yIntersect = y1 + t * (y2 - y1);
  const wIntersect = w1 + t * (w2 - w1);
  const intersection: Vector4Tuple = [
    xIntersect,
    yIntersect,
    nearZ,
    wIntersect,
  ];

  // p1이 뒤에 있으면 교차점과 p2 반환
  if (behind1) {
    return [intersection, p2];
  }

  // p2가 뒤에 있으면 p1과 교차점 반환
  return [p1, intersection];
};

export const projectPoint = (
  {
    extrinsic,
    intrinsic,
    lidarCalibration,
  }: {
    extrinsic: Extrinsic;
    intrinsic: __CES2026__CameraIntrinsic;
    lidarCalibration?: Nullish<__CES2026__LidarConfig['calibration']>;
  },
  point: Vector3Tuple,
): Nullish<Vector2Tuple> => {
  const camPoint = transformPoint(extrinsic, point, lidarCalibration);

  return projectPointFromCameraSpace(intrinsic, camPoint);
};

export const createLabelingBoxFor2DRenderer = ({
  camData: { extrinsic, intrinsic, lidarCalibration },
  labelingBoxList,
  selectedLabelingBoxIdList,
  selectedTask,
}: {
  camData: {
    extrinsic: Extrinsic;
    intrinsic: __CES2026__CameraIntrinsic;
    lidarCalibration: __CES2026__LidarConfig['calibration'];
  };
  labelingBoxList: FrameData['workData'];
  selectedLabelingBoxIdList: FrameData['workData'][0]['id'][];
  selectedTask: Task;
}) => {
  const isPointInFace = (e: MouseEvent, faceListByID: Dictionary<Path2D[]>) => {
    const ctx = (e.target as HTMLCanvasElement).getContext('2d');

    if (!ctx) return;

    const canvasBox = ctx.canvas.getBoundingClientRect();
    const orgHeight = ctx.canvas.height;
    const orgWidth = ctx.canvas.width;
    const height = ctx.canvas.offsetHeight;
    const width = ctx.canvas.offsetWidth;

    const heightRate = orgHeight / height;
    const widthRate = orgWidth / width;

    const x = (e.clientX - canvasBox.x) * widthRate;
    const y = (e.clientY - canvasBox.y) * heightRate;

    const finded = Object.entries(faceListByID).find(([, faceList]) => {
      const findedFace =
        faceList.filter(face => ctx.isPointInPath(face, x, y)).length > 0;

      return findedFace;
    });

    return finded?.[0];
  };

  const render = (
    canvas: HTMLCanvasElement,
    event?: {
      click?(e: MouseEvent, faceListByID: Dictionary<Path2D[]>): void;
      boxClick?(boxID: FrameData['workData'][0]['id']): void;
    },
  ) => {
    const ctx = canvas.getContext('2d');
    const backgroundImage = { value: null as Nullish<ImageData> };
    const faceListByID = {} as Dictionary<Path2D[]>;
    const cleanup = () => {
      canvas.removeEventListener('click', onClick);
      canvas.removeEventListener('mousemove', onMouseMove);
    };
    const onClick = (e: MouseEvent) => {
      event?.click?.(e, faceListByID);

      const findedBoxID = isPointInFace(e, faceListByID);

      if (!findedBoxID) return;

      event?.boxClick?.(findedBoxID);
    };
    const onMouseMove = (e: MouseEvent) => {
      const findedBoxID = isPointInFace(e, faceListByID);

      canvas.style.cursor = findedBoxID != null ? 'pointer' : 'default';
    };

    const result = { cleanup };

    canvas.addEventListener('click', onClick);
    canvas.addEventListener('mousemove', onMouseMove);

    if (ctx) {
      backgroundImage.value = ctx.getImageData(
        0,
        0,
        canvas.width,
        canvas.height,
      );

      labelingBoxList?.forEach(labelingBox => {
        const color =
          selectedTask.extendClass.find(item => +item.id === +labelingBox.label)
            ?.color ?? '#ffffff';
        const fillColor = new RGBA(color);
        const corner = computeBoxCorner(labelingBox);

        if (
          selectedLabelingBoxIdList.findIndex(id => labelingBox.id === id) >= 0
        ) {
          fillColor.alpha = 0.25;
        } else {
          fillColor.alpha = 0.05;
        }

        // 모든 코너를 카메라 좌표계로 변환
        const camPointList = corner.map(point =>
          transformPoint(extrinsic, point, lidarCalibration),
        );

        // 선분을 클리핑하고 투영하여 그리기
        const drawLine = (i: number, j: number) => {
          const [p1, p2] = [camPointList[i], camPointList[j]];

          // Near Plane으로 클리핑
          const clipped = clipLineToNearPlane(p1, p2);

          if (!clipped) return;

          // 클리핑된 점들을 2D로 투영
          const a = projectPointFromCameraSpace(intrinsic, clipped[0]);
          const b = projectPointFromCameraSpace(intrinsic, clipped[1]);

          if (!a || !b) return;

          ctx.beginPath();
          ctx.moveTo(a[0], a[1]);
          ctx.lineTo(b[0], b[1]);
          ctx.closePath();
          ctx.stroke();
        };

        ctx.fillStyle = fillColor.toString();
        ctx.strokeStyle = color;
        ctx.lineWidth = 4;

        const pointList = camPointList.map(camPoint =>
          projectPointFromCameraSpace(intrinsic, camPoint),
        );

        const faceList = Array.from(
          { length: pointList.length - 2 },
          () => new Path2D(),
        );

        const [p0, p1, p2, p3, p4, p5, p6, p7] = pointList;
        const pointListForFace = [
          [p0, p1, p2, p3].filter(v => v?.length === 2) as Vector2Tuple[],
          [p4, p5, p6, p7].filter(v => v?.length === 2) as Vector2Tuple[],
          [p0, p4, p5, p1].filter(v => v?.length === 2) as Vector2Tuple[],
          [p0, p3, p7, p4].filter(v => v?.length === 2) as Vector2Tuple[],
          [p1, p2, p5, p6].filter(v => v?.length === 2) as Vector2Tuple[],
          [p3, p7, p6, p2].filter(v => v?.length === 2) as Vector2Tuple[],
        ];

        pointListForFace.forEach((pointList, i) => {
          const face = faceList[i];

          pointList.forEach((v, i) => {
            face[i === 0 ? 'moveTo' : 'lineTo'](...v);
          });

          face.closePath();
        });

        ctx.fillStyle = fillColor.toString();

        faceList.forEach(face => ctx.fill(face));

        // Bottom face
        drawLine(0, 1);
        drawLine(1, 2);
        drawLine(2, 3);
        drawLine(3, 0);

        // Top face
        drawLine(4, 5);
        drawLine(5, 6);
        drawLine(6, 7);
        drawLine(7, 4);

        // Pillars
        drawLine(0, 4);
        drawLine(1, 5);
        drawLine(2, 6);
        drawLine(3, 7);

        faceListByID[labelingBox.id] = faceList;
      });
    }

    return result;
  };

  return { render };
};

export const extrinsicToMatrix = (
  ext: __CES2026__CameraExtrinsic,
): Extrinsic => {
  const { x, y, z, w, tx, ty, tz } = ext;

  const x2 = x + x;
  const y2 = y + y;
  const z2 = z + z;

  const xx = x * x2;
  const xy = x * y2;
  const xz = x * z2;
  const yy = y * y2;
  const yz = y * z2;
  const zz = z * z2;
  const wx = w * x2;
  const wy = w * y2;
  const wz = w * z2;

  // Row 1
  const r00 = 1 - (yy + zz);
  const r01 = xy - wz;
  const r02 = xz + wy;
  const r03 = tx;

  // Row 2
  const r10 = xy + wz;
  const r11 = 1 - (xx + zz);
  const r12 = yz - wx;
  const r13 = ty;

  // Row 3
  const r20 = xz - wy;
  const r21 = yz + wx;
  const r22 = 1 - (xx + yy);
  const r23 = tz;

  // Row 4 (동차 좌표계)
  const r30 = 0;
  const r31 = 0;
  const r32 = 0;
  const r33 = 1;

  return [
    [r00, r01, r02, r03],
    [r10, r11, r12, r13],
    [r20, r21, r22, r23],
    [r30, r31, r32, r33],
  ];
};
